// WeighbridgeNav — sidebar nav for the Weighbridge Management module.
//
// Sections:
//   MAIN              Dashboard / New Ticket / Open Tickets / All Tickets
//   MANAGEMENT        Customers / Vehicles / Hauliers / Drivers
//   CATALOG & PRICING Products / Price Bands / Contracts / Stock
//   REPORTS           Reports
//   SETTINGS          Settings  (single link — hub at /weighbridge/settings
//                     fans out to per-area pages; non-admin staff see locked
//                     cards rather than disappearing links)
//
// Pattern: SkipNav-style RoleGate-by-role logic with the shared
// CollectionsNav.module.css stylesheet.

import { type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Boxes,
  ClipboardList,
  FileText,
  LayoutDashboard,
  ListChecks,
  Package,
  PlusCircle,
  Receipt,
  Settings,
  SignalHigh,
  Tag,
  Truck,
  Users,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useAuthStore } from '../../../store/authStore';
import { useSidebar } from '../../../components/shell/SidebarContext';
import type { UserSummary } from '../../../types/api';
import styles from '../../collections/components/CollectionsNav.module.css';

type Role = UserSummary['role'];

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  roles?: Role[];
  end?: boolean;
}

interface NavSection {
  label?: string;
  roles?: Role[];
  items: NavItem[];
}

const STAFF_ROLES: Role[] = ['staff', 'admin'];

const SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/weighbridge/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true, roles: STAFF_ROLES },
      { to: '/weighbridge/tickets/new', label: 'New Ticket', icon: PlusCircle, roles: STAFF_ROLES },
      { to: '/weighbridge/tickets/open', label: 'Open Tickets', icon: ListChecks, roles: STAFF_ROLES },
      { to: '/weighbridge/tickets', label: 'All Tickets', icon: Receipt, end: true, roles: STAFF_ROLES },
    ],
  },
  {
    label: 'Management',
    roles: STAFF_ROLES,
    items: [
      { to: '/weighbridge/customers', label: 'Customers', icon: Users, roles: STAFF_ROLES },
      { to: '/weighbridge/vehicles', label: 'Vehicles', icon: Truck, roles: STAFF_ROLES },
      { to: '/weighbridge/hauliers', label: 'Hauliers', icon: SignalHigh, roles: STAFF_ROLES },
      { to: '/weighbridge/drivers', label: 'Drivers', icon: ClipboardList, roles: STAFF_ROLES },
    ],
  },
  {
    label: 'Catalog & Pricing',
    roles: STAFF_ROLES,
    items: [
      { to: '/weighbridge/products', label: 'Products', icon: Package, roles: STAFF_ROLES },
      { to: '/weighbridge/pricing/bands', label: 'Price Bands', icon: Tag, roles: STAFF_ROLES },
      { to: '/weighbridge/pricing/contracts', label: 'Contracts', icon: FileText, roles: STAFF_ROLES },
      { to: '/weighbridge/stock', label: 'Stock', icon: Boxes, roles: STAFF_ROLES },
    ],
  },
  // ─── SETTINGS ───────────────────────────────────────────────────────────
  // Single hub link visible to all staff. The hub page (WbSettingsPage)
  // renders every settings card; non-admins see them visually locked.
  {
    label: 'Settings',
    roles: STAFF_ROLES,
    items: [
      { to: '/weighbridge/settings', label: 'Settings', icon: Settings, roles: STAFF_ROLES },
    ],
  },
];

export function WeighbridgeNav(): JSX.Element | null {
  const role = useAuthStore((s) => s.user?.role);
  const { isCollapsed } = useSidebar();

  function visible(roles?: Role[]): boolean {
    if (!roles) return true;
    if (!role) return false;
    return roles.includes(role);
  }

  return (
    <div className={cn(styles.nav, isCollapsed && styles.collapsed)}>
      {SECTIONS.map((section, idx) => {
        if (!visible(section.roles)) return null;
        const items = section.items.filter((it) => visible(it.roles));
        if (items.length === 0) return null;
        return (
          <div key={idx} className={styles.section}>
            {section.label ? (
              <span className={styles.sectionLabel}>{section.label}</span>
            ) : null}
            <ul className={styles.list}>
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => cn(styles.link, isActive && styles.linkActive)}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <span className={styles.linkInner}>
                      <span className={styles.icon}>
                        <item.icon size={18} aria-hidden />
                      </span>
                      <span className={styles.label}>{item.label}</span>
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
